import { useContext } from "react"
import { Navigate } from "react-router-dom"
import { UserContext } from "../UserContext"
import { UserRoleContext } from "../UserRoleContext"
import { OrganizationContext } from "../OrganizationContext"

const resolveRedirect = (redirect, context) => {
  if (typeof redirect === "function") return redirect(context)
  return redirect
}

const ProtectedRoute = ({
  component: Component,
  render,
  requiresAuth = true,
  requiresOrg = true,
  featureFlag,
  allowedRoles,
  redirectUnauthenticated = "/",
  redirectNoOrg = "/welcome",
  redirectFeatureDisabled = "/",
  redirectUnauthorized,
  redirectIfAuthenticated,
  replace = true,
}) => {
  const user = useContext(UserContext)
  const role = useContext(UserRoleContext)
  const organizationContext = useContext(OrganizationContext) ?? {}

  const organizations = organizationContext.organizations ?? []
  const selectedOrgId = organizationContext.selectedOrgId ?? null

  const baseContext = {
    user,
    role,
    organizations,
    selectedOrgId,
    ...organizationContext,
  }

  const needsOrg = !!baseContext.user && baseContext.organizations.length === 0
  const isAuthenticated = !!baseContext.user

  const enhancedContext = { ...baseContext, needsOrg }

  if (!requiresAuth && isAuthenticated && redirectIfAuthenticated) {
    const target = resolveRedirect(redirectIfAuthenticated, enhancedContext)
    if (target) return <Navigate to={target} replace={replace} />
  }

  if (requiresAuth && !isAuthenticated) {
    const target = resolveRedirect(redirectUnauthenticated, enhancedContext)
    return <Navigate to={target} replace={replace} />
  }

  if (requiresOrg && needsOrg) {
    const target = resolveRedirect(redirectNoOrg, enhancedContext)
    return <Navigate to={target} replace={replace} />
  }

  if (
    allowedRoles &&
    allowedRoles.length > 0 &&
    (!enhancedContext.role || !allowedRoles.includes(enhancedContext.role))
  ) {
    const fallback = isAuthenticated
      ? needsOrg
        ? "/welcome"
        : "/dashboard"
      : redirectUnauthenticated
    const target = resolveRedirect(
      redirectUnauthorized ?? fallback,
      enhancedContext
    )
    return <Navigate to={target} replace={replace} />
  }

  const hasFeature =
    featureFlag === undefined
      ? true
      : typeof featureFlag === "function"
        ? featureFlag(enhancedContext)
        : !!featureFlag

  if (!hasFeature) {
    const target = resolveRedirect(redirectFeatureDisabled, enhancedContext)
    return <Navigate to={target} replace={replace} />
  }

  if (render) return render(enhancedContext)

  if (Component) return <Component />

  return null
}

export default ProtectedRoute
